import { http } from 'interfaces'
import { type LegRole } from 'services/lutValuesService'
import { type State } from 'components/us/common/state-select'
import { type Chamber } from 'components/us/common/chamber-select'
import {
  type LegDistrict,
  type LegStaffInformation,
  type MemberInformation
} from 'services/person/us/edit/memberInfoService'
import { type CommitteeMember as FullCommitteeMember } from 'pages/us/committee/members/commitee-members-page'
import { type Committee } from 'services/commitee/committeeListService'
import { type LeadershipOfficer } from 'services/leadership/leadershipOfficerListService'
import { type LeadershipStaffInformation } from 'services/leadership/leadershipStaffService'
import { type LegCandidate } from '../../../candidate/candidateService'

export type District = Omit<
LegDistrict,
| 'state'
| 'legchamber'
| 'type'
| 'members'
| 'counties'
| 'mapLink'
| 'updated_by'
| 'last_updated'
> & {
  district_code: string
  district_desc: string
}

export type MemberRole = Omit<
MemberInformation,
| 'person'
| 'legseat'
| 'percentVote'
| 'electDate'
| 'reelectYear'
| 'electCode'
| 'firstElectionDate'
| 'nextElectionDate'
| 'serviceStartDate'
| 'serviceEndDate'
| 'termNumber'
| 'fecLinkNum'
| 'prefAddress'
| 'pyracusc'
| 'cyracusc'
| 'pyradasc'
| 'cyradasc'
| 'pyraflsc'
| 'cyraflsc'
| 'pyruscosc'
| 'cyruscosc'
| 'updatedBy'
| 'lastUpdated'
> & {
  person_id: number
  district?: District
  state?: State
  legchamber?: Chamber
}

export type StaffRole = Omit<
LegStaffInformation,
| 'person'
| 'legrole2'
| 'legrole3'
| 'statusId'
| 'title'
| 'email'
| 'address'
| 'updatedBy'
| 'lastUpdated'
| 'responsibilities'
> & {
  legrole: LegRole
  display_order: number
  member?: {
    id: number
    person_id: number
    name?: string
    role?: string
  }
  committee?: {
    id: number
    name?: string
  }
}

export type CommitteeMember = Omit<
FullCommitteeMember,
'member' | 'legRole' | 'displayOrder' | 'updatedBy' | 'lastUpdated'
> & {
  leg_role?: LegRole
  display_order?: number
}

export type CommitteeStaffRole = Omit<
LegStaffInformation,
| 'person'
| 'legrole2'
| 'legrole3'
| 'statusId'
| 'email'
| 'address'
| 'updatedBy'
| 'lastUpdated'
| 'responsibilities'
> & {
  legrole: LegRole
  display_order: number
  committee?: Committee
  majMin?: string
}

export type LeadershipRole = Omit<
LeadershipOfficer,
| 'person'
| 'display_order'
| 'updatedBy'
| 'lastUpdated'
| 'urls'
| 'address'
| 'responsibilities'
> & {
  display_order?: number
}

export type LeadershipStaffRole = Omit<
LeadershipStaffInformation,
| 'person'
| 'role'
| 'legrole2'
| 'legrole3'
| 'statusId'
| 'title'
| 'email'
| 'address'
| 'updatedBy'
| 'lastUpdated'
| 'responsibilities'
| 'leadershipOfficer'
> & {
  legrole: LegRole
  display_order: number
  leadership_officer: {
    id: number
    ofcname?: string
    state?: {
      id: number
      state_desc: string
    }
    legchamber?: {
      id: number
      desc: string
    }
  }
}

export interface LegislativeRoles {
  member_roles: MemberRole[]
  member_staff_roles: StaffRole[]
  committee_staff_roles: StaffRole[]
  committee_members: CommitteeMember[]
  caucus_members: CommitteeMember[]
  leadership_roles: LeadershipRole[]
  leadership_staff_roles: LeadershipStaffRole[]
  leg_candidates: LegCandidate[]
}

export async function getLegislativeRoles (personId: number): Promise<LegislativeRoles> {
  const response = await http.get<LegislativeRoles>(
    `/api/v1/us/person/${personId}/legislative-roles/`
  )
  return response.data
}
